import { useState } from 'react';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, App, Button, Card, Form, Input, Typography } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '../context/AuthContext';

interface LoginFormValues {
  email: string;
  password: string;
}

/** Primer factor del login: email + password. Nunca abre sesión: deriva al 2FA. */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fallback del banner: si `/brand/login-hero.png` no existe, no ocupa espacio.
  const [hasHero, setHasHero] = useState(true);

  const from = (location.state as { from?: string } | null)?.from ?? '/noc';

  const handleSubmit = async (values: LoginFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await login(values.email, values.password);
      if (response.requireSetup) {
        message.warning('Debes configurar el doble factor (2FA) antes de continuar.');
        navigate('/login/enroll', { replace: true });
        return;
      }
      if (response.pendingTotp && response.userId) {
        navigate('/login/2fa', { replace: true, state: { from } });
        return;
      }
      setError('El servidor no solicitó el segundo factor. Contacta a soporte.');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) {
        setError('Demasiados intentos. Espera unos minutos e intenta de nuevo.');
      } else if (caught instanceof ApiError) {
        setError(caught.message);
      } else {
        setError('No se pudo iniciar sesión. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-shell">
      <Card className="auth-card">
        {hasHero && (
          <img
            src="/brand/login-hero.png"
            alt=""
            className="auth-brand__image"
            onError={() => setHasHero(false)}
          />
        )}
        <div className="auth-brand">
          <span className="auth-brand__text">
            xGo<strong>Electric</strong>
          </span>
        </div>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          Iniciar sesión
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          Acceso al backoffice de operación.
        </Typography.Paragraph>

        {error && (
          <Alert
            type="error"
            showIcon
            closable
            message={error}
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form<LoginFormValues> layout="vertical" onFinish={handleSubmit} requiredMark={false}>
          <Form.Item
            label="Correo"
            name="email"
            rules={[
              { required: true, message: 'Ingresa tu correo' },
              { type: 'email', message: 'Correo inválido' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="usuario@goelectric.co"
              autoComplete="username"
              size="large"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            label="Contraseña"
            name="password"
            rules={[{ required: true, message: 'Ingresa tu contraseña' }]}
          >
            <Input.Password prefix={<LockOutlined />} autoComplete="current-password" size="large" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
              Ingresar
            </Button>
          </Form.Item>
        </Form>

        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          ¿Primer acceso o invitación? <Link to="/login/enroll">Configurar 2FA</Link>
        </Typography.Paragraph>
      </Card>
    </div>
  );
}
